import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Checkbox, Palette, SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type CheckboxValue = string | number;
export type CheckboxSize = 'small' | 'medium';
export type LabelPlacement = 'end' | 'start';

export interface CustomCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean, value?: CheckboxValue) => void;
  label?: React.ReactNode;
  value?: CheckboxValue;
  name?: string;
  disabled?: boolean;
  size?: CheckboxSize;
  labelPlacement?: LabelPlacement;
  sx?: SxProps<Theme>;
  checkboxSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
}

const sizeMap: Record<CheckboxSize, { box: number; icon: number }> = {
  small: { box: 18, icon: 14 },
  medium: { box: 22, icon: 16 },
};

interface CheckboxVisualProps {
  checked: boolean;
  disabled: boolean;
  size: CheckboxSize;
  colors: Palette;
}

const CheckboxVisual: React.FC<CheckboxVisualProps> = ({ checked, disabled, size, colors }) => {
  const sizes = sizeMap[size];

  const backgroundColor = disabled
    ? colors.checkboxGroup.checkboxUncheckedBackground
    : checked
    ? colors.checkboxGroup.checkboxCheckedBackground
    : colors.checkboxGroup.checkboxUncheckedBackground;

  const borderColor = disabled
    ? colors.checkboxGroup.disabledBorder
    : colors.checkboxGroup.checkboxBorder;

  return (
    <Box
      component="span"
      sx={{
        width: sizes.box,
        height: sizes.box,
        borderRadius: '2px',
        border: '1px solid',
        borderColor,
        backgroundColor,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        transition: 'all 0.2s ease',
      }}
    >
      {checked ? (
        <CheckIcon
          sx={{
            fontSize: sizes.icon,
            color: colors.checkboxGroup.checkboxCheckIcon,
            fill: colors.checkboxGroup.checkboxCheckIcon,
            opacity: 1,
            fontWeight: 700,
          }}
        />
      ) : null}
    </Box>
  );
};

const getWrapperDirection = (labelPlacement: LabelPlacement) => {
  switch (labelPlacement) {
    case 'start':
      return 'row-reverse';
    case 'end':
    default:
      return 'row';
  }
};

const CustomCheckbox: React.FC<CustomCheckboxProps> = ({
  checked,
  onChange,
  label,
  value,
  name,
  disabled = false,
  size = 'medium',
  labelPlacement = 'end',
  sx,
  checkboxSx,
  labelSx,
}) => {
  const theme = useTheme();
  const colors = theme.palette;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked, value);
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: getWrapperDirection(labelPlacement),
        alignItems: 'center',
        gap: 1,
        ...((sx as object) || {}),
      }}
    >
      <Checkbox
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        name={name}
        disableRipple
        icon={<CheckboxVisual checked={false} disabled={disabled} size={size} colors={colors} />}
        checkedIcon={<CheckboxVisual checked disabled={disabled} size={size} colors={colors} />}
        sx={{
          padding: 0,
          '&:hover': {
            backgroundColor: 'transparent',
          },
          ...((checkboxSx as object) || {}),
        }}
      />

      {label ? (
        <Box
          component="span"
          sx={{
            color: colors.checkboxGroup.optionText,
            fontSize: '14px',
            lineHeight: 1.2,
            ...((labelSx as object) || {}),
          }}
        >
          {label}
        </Box>
      ) : null}
    </Box>
  );
};

export default CustomCheckbox;
